import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { Inputs } from '@/components/shared/forms/Inputs'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'
import { CallTechUserReport } from '@/services/backend/config/entities'
import { Repository } from '@/services/repository'

type Props = {
  data: CallTechUserReport
  onSuccess: (data: CallTechUserReport) => void
}

function useFormHook({ data, onSuccess }: Props) {
  const { user } = useAuthStore()
  const { Edit } = Repository.Entity.CallTech
  const queryClient = useQueryClient()

  const form = useForm<Parameters<typeof Edit>[0]>({
    defaultValues: data,
  })

  const onSubmit = form.handleSubmit(async (data) => {
    if (!user) return toast.error('Usuário não encontrado')

    try {
      const res = await Edit({ ...data, techUserId: user.id })
      toast.success('Atendimento encerrado com sucesso')
      onSuccess(res)
      queryClient.invalidateQueries([
        'useGetCallDetails',
        { callId: data.callId },
      ])
    } catch (error: any) {
      toast.error(error.message)
    }
  })

  return { form, onSubmit }
}

function Form({ data, onSuccess }: Props) {
  const { form, onSubmit } = useFormHook({ data, onSuccess })

  const [checkList, setCheckList] = useState({
    evidence: false,
    products: false,
  })

  const allChecked = checkList.products && checkList.evidence

  return (
    <form onSubmit={onSubmit}>
      <VStack align="stretch" gap={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Parecer técnico</FormLabel>
          <Inputs.Textarea useForm={form} useFormField="description" />
        </FormControl>

        <Checkbox
          checked={checkList.evidence}
          onChange={() =>
            setCheckList((current) => ({
              ...current,
              evidence: !current.evidence,
            }))
          }
        >
          Anexei todas as evidências
        </Checkbox>

        <Checkbox
          checked={checkList.products}
          onChange={() =>
            setCheckList((current) => ({
              ...current,
              products: !current.products,
            }))
          }
        >
          Realizei o consumo de produtos corretamente
        </Checkbox>

        <HStack justifyContent="space-between" mt={8}>
          <Button type="submit" colorScheme="primary" isDisabled={!allChecked}>
            Encerrar atendimento
          </Button>
        </HStack>
      </VStack>
    </form>
  )
}

export default function Component({ data, onSuccess }: Props) {
  const { user } = useAuthStore()
  const { isOpen, onOpen, onClose } = useDisclosure()

  if (user?.id !== data.techUserId) return null

  return (
    <>
      <Button colorScheme="primary" onClick={onOpen}>
        Encerrar atendimento
      </Button>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Form
              data={data}
              onSuccess={(res) => {
                onClose()
                if (onSuccess) onSuccess(res)
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
